// @flow
import React from 'react';
import get from 'lodash/get';
import head from 'lodash/head';
import { FormattedMessage } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { formatSecondsToRelativeTime } from '../../utils/strings';
import Image from '../../components/shared/Images/Image';
import Icon from '../../components/shared/Icons/Icon';
import messages from '../../common/messages';
import styles, { inlineStyles } from '../shared/styles';
import LocationContainer from '../../common/Location/LocationContainer';
import { Features } from '../../components/mobile/Features';
import { Price } from '../../components/shared/Labels';
import { IMAGE_SIZE_640PX } from '../../components/shared/Images/Image/constants';
import sortImagesByPreferredSize from '../../components/shared/Images/Image/utils/sortImagesByPreferredSize';

const { newAd, unavailablePrice } = messages;

type Props = {
  ad: ?Object,
  countryCode: string,
  readingDirection: string,
  verticalName: VerticalName,
  intl: IntlShape,
  handleItemClick: Function,
};

export default function HomesFeaturedAd({
  ad,
  countryCode,
  readingDirection,
  verticalName,
  intl,
  handleItemClick,
}: Props) {
  if (!ad) {
    return null;
  }

  const redirectUrl = ad.redirectUrl;
  const wasVisited = false;
  const title = ad.title;
  const timeAgoSeconds = ad.timeAgoSeconds;
  const verticalProperties = ad.verticalProperties;
  const images = sortImagesByPreferredSize(verticalProperties.images, IMAGE_SIZE_640PX);
  const totalImages = verticalProperties.totalImages;
  const location = ad.location;
  const price = verticalProperties.price;

  const canShowLocation =
    location.city ||
    location.region ||
    location.cityArea ||
    verticalProperties.address ||
    verticalProperties.geoInfo;

  return (
    <div className="featured-snippet">
      <a href={redirectUrl} onClick={handleItemClick}>
        <article className={wasVisited ? 'visited' : ''}>
          <div className="img">
            {images && images.length ? (
              <Image image={head(images)} preferredSize={IMAGE_SIZE_640PX} />
            ) : (
              <span className="placeholder">
                <span>
                  <Icon style={inlineStyles.photosIcon} width={50} type={verticalName} />
                </span>
              </span>
            )}

            {totalImages && totalImages > 1 ? (
              <span className="photos">
                <Icon style={inlineStyles.photosIcon} type="photos" />
                {totalImages}
              </span>
            ) : null}

            <span className="new">
              <FormattedMessage {...newAd} />
            </span>
          </div>
          <div className="content">
            <h2 itemProp="description">{title}</h2>
            <div className="location">
              {canShowLocation && (
                <LocationContainer
                  region={location.region}
                  city={location.city}
                  cityArea={location.cityArea}
                  address={verticalProperties.address}
                  geoInfo={{
                    geoOsm: verticalProperties.geoOsm,
                    numVisibleItems: -1,
                    postalCode: location.postalCode,
                  }}
                />
              )}
            </div>

            <div className="middle-info">
              <Features
                verticalName={verticalName}
                verticalProps={verticalProperties}
                country={{
                  code: countryCode,
                  readingDirection,
                }}
                description={ad.description}
              />
              {price.value ? (
                <Price
                  value={price.value}
                  valueFormatted={price.valueFormatted}
                  oldValue={price.oldValue}
                  oldValueFormatted={price.oldValueFormatted}
                />
              ) : (
                <p className="noPrice">
                  <FormattedMessage {...unavailablePrice} />
                </p>
              )}
            </div>

            <div className="bottom-info">
              <span className="date">{formatSecondsToRelativeTime(timeAgoSeconds, intl)}</span>
              <span className="source-label">{get(ad, ['source', 'name'])}</span>
            </div>
          </div>
        </article>
      </a>
      <style jsx>{styles}</style>
    </div>
  );
}



// WEBPACK FOOTER //
// ./app/FeaturedAd/homes/index.js